import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, SearchX } from "lucide-react";
import { API } from "@/lib/api";
import Logo from "@/components/Logo";
import PresentationViewer from "@/components/PresentationViewer";
import { Button } from "@/components/ui/button";

function ViewerError({ icon: Icon, title, message }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid="viewer-error"
      className="fixed inset-0 bg-background bg-grid flex flex-col items-center justify-center text-center px-6"
    >
      <Logo className="h-9 mb-10" />
      <div className="h-16 w-16 rounded-2xl bg-white/5 border border-white/10 grid place-items-center mb-6">
        <Icon className="h-8 w-8 text-brand-blue" />
      </div>
      <h1 className="font-heading text-3xl font-bold text-white tracking-tight">{title}</h1>
      <p className="text-muted-foreground mt-3 max-w-md">{message}</p>
      <Button
        onClick={() => navigate("/")}
        className="mt-8 rounded-full bg-brand-blue hover:bg-brand-bluehover text-white h-11 px-6"
      >
        Go to portal
      </Button>
      <p className="text-xs text-muted-foreground mt-10">EntroLabs IT Solutions</p>
    </div>
  );
}

export default function PublicViewerPage() {
  // const { slug } = useParams();
  const slug = "ppt";
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ status: "loading", data: null });

  const slideParam = parseInt(searchParams.get("slide"), 10);
  const initialIndex = Number.isFinite(slideParam) && slideParam > 0 ? slideParam - 1 : 0;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/presentations/${slug}`);
        if (mounted) setState({ status: "ok", data });
      } catch (e) {
        const code = e?.response?.status;
        if (mounted)
          setState({ status: code === 403 ? "private" : "notfound", data: null });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (state.status === "ok") document.title = `${state.data.title} · EntroLabs`;
  }, [state]);

  if (state.status === "loading") {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  if (state.status === "notfound") {
    return (
      <ViewerError
        icon={SearchX}
        title="Presentation not found"
        message="The presentation you're looking for doesn't exist or may have been removed."
      />
    );
  }

  if (state.status === "private") {
    return (
      <ViewerError
        icon={Lock}
        title="Not publicly available"
        message="This presentation is not publicly available. Please contact the presenter for access."
      />
    );
  }

  return (
    <PresentationViewer
      slides={state.data.slides}
      title={state.data.title}
      initialIndex={initialIndex}
      showBrand
      autoPlay
      autoPlayInterval={10000}
      showPlayButton={false}
      areaNav={false}
      autoFullscreen
    />
  );
}
